/**
 * OnStreamAlerts — viewer-facing HUD overlay that pops animated
 * banners when someone follows the host or drops a tip during a live
 * stream. Polls `GET /api/live/{streamId}/alerts?since=iso` every 4s.
 *
 * Positioned as an absolutely-positioned layer inside the video shell
 * so it sits above the video but below chat + toolbars. Fires up to 3
 * concurrent banners (queue drains oldest first) so a burst of tips
 * doesn't visually pile up forever.
 */
import React, { useEffect, useRef, useState } from "react";
import { Heart, DollarSign, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import api from "../lib/api";

const MAX_VISIBLE = 3;
const DISPLAY_MS = 4200;
const POLL_MS = 4000;

// Cached WebAudio nodes for the tip chime — created lazily on first
// tip so we don't ask for AudioContext permission until we need it.
// The chime is a 3-note bell arpeggio (C6 → E6 → G6) with a short
// decay envelope — subtle, congratulatory, matches Twitch's "cheer"
// vibe without being disruptive to the host's own broadcast audio.
let _tipAudioCtx = null;
function playTipChime() {
  try {
    if (!_tipAudioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      _tipAudioCtx = new Ctx();
    }
    const ctx = _tipAudioCtx;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const notes = [1046.5, 1318.5, 1568]; // C6, E6, G6
    notes.forEach((freq, i) => {
      const t0 = ctx.currentTime + i * 0.08;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t0);
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.18, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.4);
    });
  } catch { /* audio blocked before user gesture */ }
}

function fireTipConfetti() {
  try {
    // Two staggered bursts from bottom-left + bottom-right so the
    // confetti fans up across the whole stream area, TikTok gift-style.
    confetti({
      particleCount: 60,
      spread: 55,
      origin: { x: 0.15, y: 0.9 },
      angle: 60,
      colors: ["#F5C445", "#FF6B9D", "#22D3EE", "#34D399", "#FFFFFF"],
      zIndex: 60,
      disableForReducedMotion: true,
    });
    confetti({
      particleCount: 60,
      spread: 55,
      origin: { x: 0.85, y: 0.9 },
      angle: 120,
      colors: ["#F5C445", "#FF6B9D", "#22D3EE", "#34D399", "#FFFFFF"],
      zIndex: 60,
      disableForReducedMotion: true,
    });
  } catch { /* confetti optional */ }
}

export default function OnStreamAlerts({ streamId }) {
  const [banners, setBanners] = useState([]);
  const sinceRef = useRef(new Date().toISOString());
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!streamId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/live/${streamId}/alerts?since=${encodeURIComponent(sinceRef.current)}`);
        const alerts = Array.isArray(data?.alerts) ? data.alerts : [];
        if (cancelled || alerts.length === 0) return;
        const fresh = [];
        for (const a of alerts) {
          const key = `${a.type}-${a.from_user_id}-${a.created_at}`;
          if (seenRef.current.has(key)) continue;
          seenRef.current.add(key);
          fresh.push({ ...a, _key: key });
          if (a.created_at && a.created_at > sinceRef.current) {
            sinceRef.current = a.created_at;
          }
        }
        if (fresh.length === 0) return;
        setBanners((cur) => [...cur, ...fresh].slice(-MAX_VISIBLE));
        // Celebrate tips with a confetti burst + soft chime. Follows
        // just get the banner — tips get the whole party. Keeps the
        // "tip = special" psychology TikTok / Twitch have trained.
        const gotTip = fresh.some((a) => a.type === "tip");
        if (gotTip) {
          fireTipConfetti();
          playTipChime();
        }
        // Auto-dismiss each new banner after DISPLAY_MS.
        fresh.forEach((a) => {
          setTimeout(() => {
            setBanners((cur) => cur.filter((b) => b._key !== a._key));
          }, DISPLAY_MS);
        });
      } catch { /* stream may not exist yet */ }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [streamId]);

  if (banners.length === 0) return null;

  return (
    <div
      data-testid="on-stream-alerts"
      className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 pointer-events-none"
    >
      {banners.map((b) => (
        <AlertBanner key={b._key} alert={b} />
      ))}
    </div>
  );
}

function AlertBanner({ alert }) {
  const isTip = alert.type === "tip";
  const Icon = isTip ? DollarSign : Heart;
  const iconBg = isTip ? "from-emerald-500 to-emerald-700" : "from-rose-500 to-rose-700";
  const line1 = isTip ? `${alert.from_user_name} tipped $${(alert.amount || 0).toFixed(2)}` : `${alert.from_user_name} followed`;
  const line2 = isTip && alert.message ? `"${alert.message}"` : (isTip ? "Thanks for the support!" : "New follower!");
  return (
    <div
      data-testid={`stream-alert-${alert.type}`}
      className="pointer-events-auto bg-black/85 backdrop-blur-xl border border-white/15 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex items-center gap-3 px-4 py-2.5 min-w-[280px] max-w-md"
      style={{ animation: "gs-count-pop 400ms ease-out forwards" }}
    >
      <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${iconBg} flex items-center justify-center shrink-0 shadow-lg`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-black text-sm truncate flex items-center gap-1">
          {line1}
          <Sparkles className="w-3 h-3 text-tv-accent shrink-0" />
        </div>
        <div className="text-white/70 text-[11px] truncate">{line2}</div>
      </div>
    </div>
  );
}
